import { useGetBoxPlotDataQuery } from '../../../../services/mVPGraphQlService';
import {
  AppReactECharts,
  AutoCompleteController,
} from '../../../../components';
import { AppCircularProgress } from '../../../../components/common/loaders/AppCircularProgress';
import * as yup from 'yup';
import { useFormWithSchema } from '../../../../utils/functions/yupFormTypescript';
import dayjs from 'dayjs';
import { Box, Grid, Typography } from '@mui/material';
// import { useGetFilterDataForMapQuery } from '../../../../services/mVPMapService';
import { useEffect } from 'react';
import { MVPFiltersProps } from '../../constants';
import { useFilterContext } from '../../../../services/mVPMapService/filterContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import React from 'react';
import { useDebounce } from '../../../../services/customHook/debounceHook';
import { useAlertHelper } from '../../../../utils/functions/hooks';

const X_AXIS_TYPE_OPTIONS = [
  {
    value: 'region',
    label: 'Region',
  },
  {
    value: 'people',
    label: 'People',
  },
];

const formSchema = yup.object({
  xAxisType: yup.string().required('Chart by is required'),
});

export const BoxplotChart: React.FC<MVPFiltersProps> = ({
  dateOfData,
  date,
  intValue,
  includeDatesList,
}) => {
  const {
    control,
    formState: { errors },

    watch,
  } = useFormWithSchema(formSchema, {
    mode: 'onTouched',
    defaultValues: {
      xAxisType: 'region',
    },
  });
  const { filterValues: commonFilters } =
    useFilterContext();
  const wsId = useSelector((state: RootState) => state.country.wsId);
  const { showError } = useAlertHelper();

  const selectedDate = dayjs(dateOfData);
  const startTime = Math.floor(
    Number(selectedDate?.startOf('day').utc()) / 1000
  );
  const endTime = Math.floor(
    Number(selectedDate?.endOf('day').utc()) / 1000
  );
  const debouncedIntValue = useDebounce(
    Number(intValue),
    1000
  );

  const shouldFetchData =
    typeof debouncedIntValue === 'number' &&
    debouncedIntValue <= 200;

  const { data: plotData, isFetching: isLoadingPlotData } =
    useGetBoxPlotDataQuery(
      {
        commonFilters,
        wsId,
        startTime: startTime || 0,
        endTime: endTime || 0,
        xAxisType: watch('xAxisType'),
        tableDataType: date,
        daysAfterPlanting: debouncedIntValue,
      },
      {
        skip:
          !shouldFetchData ||
          (date == 'CUSTOM_DATE' && !startTime && !endTime),
      }
    );
  useEffect(() => {
    if (debouncedIntValue > 200) {
      showError('Number of days cannot exceed 200');
      return;
    }
  }, [debouncedIntValue]);

  const boxplotDetailsList =
    plotData?.data?.GetBoxPlotDetailsForDateRange
      ?.boxplotDetails;

  const dataSet = {
    boxplot: boxplotDetailsList?.map(
      ({
        xAxisValue,
        lowerBound,
        q1,
        median,
        q3,
        upperBound,
      }) => [
        xAxisValue,
        lowerBound,
        q1,
        median,
        q3,
        upperBound,
      ]
    ),
    outliers: boxplotDetailsList?.map(
      ({ xAxisValue, outliers }) => [
        xAxisValue,
        ...[
          ...outliers.map(
            (outlier) => outlier.outlierValue
          ),
        ],
      ]
    ),
  };

  const noDataFieldNos =
    plotData?.data?.GetBoxPlotDetailsForDateRange
      ?.noDataFieldNos;

  const filteredFieldNos =
    plotData?.data?.GetBoxPlotDetailsForDateRange
      ?.filteredFieldNos;

  const options = {
    dataset: [
      {
        source: dataSet.boxplot || [],
      },
      {
        source: dataSet.outliers || [],
      },
    ],
    tooltip: {
      trigger: 'item',
      axisPointer: {
        type: 'shadow',
      },
    },
    grid: {
      left: '10%',
      right: '10%',
      bottom: '15%',
    },
    xAxis: {
      type: 'category',
      name: plotData?.data?.GetBoxPlotDetailsForDateRange
        ?.xAxisTypeName,
      nameTextStyle: {
        fontWeight: 'bolder',
        fontSize: '14',
      },
    },
    yAxis: {
      name: commonFilters?.index,
      nameTextStyle: {
        fontWeight: 'bolder',
        fontSize: '14',
      },
    },
    series: [
      {
        name: 'boxplot',
        type: 'boxplot',
        datasetIndex: 0,
      },
      {
        name: 'outlier',
        type: 'scatter',
        datasetIndex: 1,
      },
    ],
    dataZoom: [
      {
        type: 'slider',
        xAxisIndex: 0,
        filterMode: 'none',
        minValueSpan: 1, // Ensure at least 1 units remain visible on zoom
      },
    ],
  };

  return (
    <div>
      {/* <Grid container spacing={3}>
        <Grid
          item
          xs={6}
          sm={6}
          md={2}
          lg={2}
          className="field-grid">
          <AutoCompleteController
            label="Chart by"
            name="xAxisType"
            control={control}
            options={X_AXIS_TYPE_OPTIONS}
            error={errors.xAxisType?.message}
            disableClearable
            required
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          lg={6}
          className="exclude-text field-grid">
          {noDataFieldNos?.length
            ? noDataFieldNos?.length
            : 0}{' '}
          farms excluded on selected date (out of{' '}
          {filteredFieldNos?.length
            ? filteredFieldNos?.length
            : 0}
          )
        </Grid>
      </Grid> */}

      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{
          mb: 1.5,
          px: 1,
        }}>
        <Grid item>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              display: 'inline-block',
            }}>
            Box Plot{' '}
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 400 }}>
              [
              {`${
                noDataFieldNos?.length || 0
              } farms excluded on selected date (out of ${
                filteredFieldNos?.length || 0
              })`}
              ]
            </Typography>
          </Typography>
        </Grid>

        <Grid
          item
          xs="auto"
          sx={{ minWidth: 220 }} // optional fixed width
        >
          <AutoCompleteController
            label="Type"
            name="xAxisType"
            control={control}
            options={X_AXIS_TYPE_OPTIONS}
            error={errors.xAxisType?.message}
            disableClearable
            required
            height={40}
          />
        </Grid>
      </Grid>

      {isLoadingPlotData ? (
        <AppCircularProgress />
      ) : (
        <div>
          {/* {boxplotDetailsList?.length ? (
            <AppReactECharts
              option={options}
              startTime={startTime}
              endTime={endTime}
              commonFilters={commonFilters}
            />
          ) : (
            <Box className="no-data-text text-center">
              <Box className="no-data-text text-center">
                {date === 'CUSTOM_DATE' &&
                includeDatesList &&
                includeDatesList?.length > 0
                  ? 'Please select the date'
                  : ' No Data Found In Graph'}
              </Box>
            </Box>
          )} */}

          {date === 'CUSTOM_DATE' &&
          includeDatesList &&
          includeDatesList?.length > 0 ? (
            boxplotDetailsList?.length ? (
              <AppReactECharts
                option={options}
                startTime={startTime}
                endTime={endTime}
                commonFilters={commonFilters}
              />
            ) : (
              <Box className="no-data-text text-center">
                Please select the date
              </Box>
            )
          ) : date == 'DAY_AFTER_PLANTING_TYPE' &&
            boxplotDetailsList?.length ? (
            <AppReactECharts
              option={options}
              startTime={startTime}
              endTime={endTime}
              commonFilters={commonFilters}
            />
          ) : (
            <Box className="no-data-text text-center">
              No Data Found In Graph
            </Box>
          )}

          {/* <MVPTable
            fieldNumbers={noDataFieldNos}
            startTime={
              boxplotDetailsList?.length
                ? startTime
                : undefined
            }
            endTime={
              boxplotDetailsList?.length
                ? endTime
                : undefined
            }
            commonFilters={commonFilters}
            type={'BOXPLOT_TAB'}
            dataType={watch('date')}
            daysAfterPlantingValue={debouncedIntValue}
          /> */}
        </div>
      )}
    </div>
  );
};
